import Loader from '@components/ui/loader'
import { useMutation } from '@connectrpc/connect-query'
import { parseErrorMessage } from '@lib/errors'
import { UserRound } from 'lucide-react'
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  listFollowers,
  listFollowing,
} from '@gen/api/v1/api-ApiService_connectquery'
import { User } from '@gen/api/v1/api_pb'

export enum ProfileFollowersType {
  Followers = 'followers',
  Following = 'following',
}

interface ProfileFollowersProps {
  setOpen: Dispatch<SetStateAction<boolean>>
  type: ProfileFollowersType
  userId: string
}

const ProfileFollowers: FC<ProfileFollowersProps> = ({
  setOpen,
  userId,
  type,
}) => {
  const [followers, setFollowers] = useState<User[]>([])

  const listFollowersMutation = useMutation(listFollowers)
  const listFollowingMutation = useMutation(listFollowing)

  useEffect(() => {
    if (userId && type) {
      ;(async () => {
        try {
          if (type === ProfileFollowersType.Followers) {
            const followersRes = await listFollowersMutation.mutateAsync({
              id: userId,
            })

            if (followersRes.followers) {
              setFollowers(followersRes.followers)
            }
          } else if (type === ProfileFollowersType.Following) {
            const followingRes = await listFollowingMutation.mutateAsync({
              id: userId,
            })

            if (followingRes.following) {
              setFollowers(followingRes.following)
            }
          }
        } catch (error) {
          const message = parseErrorMessage(error)
          toast.error(`Failed to fetch ${type}`, {
            description: message,
          })
        }
      })()
    }
  }, [userId, type])

  return (
    <div className="mt-4">
      {followers ? (
        <>
          {followers.length > 0 ? (
            <>
              {followers.map((follower) => (
                <a
                  key={follower.id}
                  onClick={() => setOpen(false)}
                  href={`/profile/${follower.username}`}
                >
                  <div className="flex justify-start items-center py-2">
                    {follower?.profilePictureUrl ? (
                      <img
                        className="rounded-full w-10 h-10"
                        src={follower.profilePictureUrl}
                      />
                    ) : (
                      <div className="rounded-full w-10 h-10 flex items-center justify-center bg-muted text-muted-foreground overflow-hidden">
                        <UserRound className="w-10 h-10" />
                      </div>
                    )}
                    <div className="ml-2">
                      <div className="text-sm font-semibold">
                        @{follower.username}
                      </div>
                      {follower.displayName && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {follower.displayName}
                        </div>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </>
          ) : (
            <div className="text-muted-foreground text-sm">
              {type === ProfileFollowersType.Followers
                ? 'No followers'
                : 'Not following anyone'}
            </div>
          )}
        </>
      ) : (
        <div className="text-center p-12 w-full">
          <Loader />
        </div>
      )}
    </div>
  )
}

export default ProfileFollowers
